import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { YikiRoundPanel } from '../components/yiki/YikiRoundPanel';
import { YikiBetForm } from '../components/yiki/YikiBetForm';
import { YikiBetHistory } from '../components/yiki/YikiBetHistory';
import { YikiWalletPanel } from '../components/yiki/YikiWalletPanel';
import { yikiService } from '../services/yikiService';
import { useWallet } from '../context/WalletContext';
import { getApiMessage, showError, showSuccess } from '../utils/swal';

const POLL_MS = 2000;

export function YikiRoomPage() {
  const { roomId } = useParams();
  const { balance, setBalance, refreshWallet } = useWallet();
  const [roomMeta, setRoomMeta] = useState(null);
  const [roundData, setRoundData] = useState(null);
  const [drawHistory, setDrawHistory] = useState([]);
  const [bets, setBets] = useState([]);
  const [betMessage, setBetMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const loadRoomData = useCallback(async (id) => {
    const [roundRes, historyRes, betsRes] = await Promise.all([
      yikiService.getActiveRound(id),
      yikiService.getDrawHistory(id),
      yikiService.getBets(id),
    ]);
    setRoundData(roundRes);
    setRoomMeta(roundRes.room);
    setDrawHistory(historyRes);
    setBets(betsRes);
    setNotFound(false);
  }, []);

  useEffect(() => {
    if (!roomId) return undefined;

    loadRoomData(roomId).catch(() => setNotFound(true));
    const timer = setInterval(() => {
      loadRoomData(roomId).catch(() => {});
    }, POLL_MS);

    return () => clearInterval(timer);
  }, [roomId, loadRoomData]);

  const handlePlaceBet = async (payload) => {
    setIsSubmitting(true);
    setBetMessage('');

    const result = await yikiService.placeBet(payload).catch(async (error) => {
      const message = getApiMessage(error, 'ไม่สามารถแทงได้');
      setBetMessage(message);
      await showError({ title: 'แทงไม่สำเร็จ', text: message });
      return null;
    });

    if (result) {
      setBalance(result.balance);
      setBetMessage(result.message);
      await showSuccess({
        title: 'แทงสำเร็จ',
        text: result.message || 'แทงยี่กีสำเร็จ',
      });
      await loadRoomData(roomId);
      refreshWallet().catch(() => {});
      setIsSubmitting(false);
      return true;
    }

    setIsSubmitting(false);
    return false;
  };

  if (notFound) {
    return <Navigate to="/yiki" replace />;
  }

  const room = roomMeta || roundData?.room;

  return (
    <div className="space-y-6">
      <Link
        to="/yiki"
        className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 transition-colors hover:text-brand-red"
      >
        <ArrowLeft className="h-4 w-4" />
        กลับหน้ารวมห้องยี่กี
      </Link>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <YikiRoundPanel
            room={room}
            round={roundData?.round}
            remainingTime={roundData?.remainingTime}
            drawHistory={drawHistory}
          />
          <YikiBetForm
            room={room}
            balance={balance}
            onPlaceBet={handlePlaceBet}
            isSubmitting={isSubmitting}
            message={betMessage}
          />
        </div>

        <div className="space-y-6">
          <YikiWalletPanel />
          <YikiBetHistory bets={bets} />
        </div>
      </div>
    </div>
  );
}
